import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Shield,
  Users,
  UserPlus,
  LogOut,
  LayoutDashboard,
  ShoppingBag,
  CreditCard,
  ScrollText,
  User,
  FileText,
  Trash2,
  Edit,
  LogIn,
  Key,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const ACTION_CONFIG = {
  user_login: { label: "User Login", icon: LogIn, color: "text-blue-400" },
  asset_deleted: { label: "Asset Deleted", icon: Trash2, color: "text-red-400" },
  document_added: { label: "Document Added", icon: FileText, color: "text-emerald-400" },
  password_reset: { label: "Password Reset", icon: Key, color: "text-amber-400" },
  admin_password_reset: { label: "Admin Password Reset", icon: Key, color: "text-violet-400" },
  admin_user_update: { label: "Admin: User Updated", icon: Edit, color: "text-blue-400" },
  admin_service_update: { label: "Admin: Service Updated", icon: ShoppingBag, color: "text-emerald-400" },
  admin_created: { label: "Admin Created", icon: UserPlus, color: "text-violet-400" },
  admin_deleted: { label: "Admin Deleted", icon: Trash2, color: "text-red-400" },
};

const AdminAuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [skip, setSkip] = useState(0);
  const [filterAction, setFilterAction] = useState("all");
  const [filterActor, setFilterActor] = useState("all");
  const navigate = useNavigate();
  const LIMIT = 30;

  const fetchLogs = useCallback(async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: skip.toString(), limit: LIMIT.toString() });
      if (filterAction !== "all") params.append("action", filterAction);
      if (filterActor !== "all") params.append("actor_type", filterActor);
      const res = await axios.get(`${API}/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [skip, filterAction, filterActor, navigate]);

  useEffect(() => {
    const adminData = localStorage.getItem("adminData");
    const token = localStorage.getItem("adminToken");
    if (!token || !adminData) { navigate("/admin/login"); return; }
    setAdmin(JSON.parse(adminData));
    fetchLogs();
  }, [navigate, fetchLogs]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login");
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("en-NG", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-400" />
              </div>
              <span className="text-lg font-bold text-white">Admin Portal</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-sm hidden sm:block">{admin?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to="/admin/dashboard">
            <Button variant="outline" className="btn-secondary"><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</Button>
          </Link>
          <Link to="/admin/users">
            <Button variant="outline" className="btn-secondary"><Users className="w-4 h-4 mr-2" />Users</Button>
          </Link>
          <Link to="/admin/requests">
            <Button variant="outline" className="btn-secondary"><ShoppingBag className="w-4 h-4 mr-2" />Requests</Button>
          </Link>
          <Link to="/admin/transactions">
            <Button variant="outline" className="btn-secondary"><CreditCard className="w-4 h-4 mr-2" />Transactions</Button>
          </Link>
          {admin?.role === "super_admin" && (
            <Link to="/admin/admins">
              <Button variant="outline" className="btn-secondary"><UserPlus className="w-4 h-4 mr-2" />Admins</Button>
            </Link>
          )}
          <Link to="/admin/audit">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white"><ScrollText className="w-4 h-4 mr-2" />Audit Log</Button>
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Audit Log</h1>
            <p className="text-slate-400 mt-1">Track all system activities — user actions, admin changes, asset modifications</p>
          </div>
          <div className="flex gap-3">
            <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setSkip(0); }}>
              <SelectTrigger className="w-48 input-default" data-testid="audit-action-filter">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="user_login">User Login</SelectItem>
                <SelectItem value="asset_deleted">Asset Deleted</SelectItem>
                <SelectItem value="document_added">Document Added</SelectItem>
                <SelectItem value="password_reset">Password Reset</SelectItem>
                <SelectItem value="admin_user_update">Admin: User Update</SelectItem>
                <SelectItem value="admin_service_update">Admin: Service Update</SelectItem>
                <SelectItem value="admin_created">Admin Created</SelectItem>
                <SelectItem value="admin_deleted">Admin Deleted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterActor} onValueChange={(v) => { setFilterActor(v); setSkip(0); }}>
              <SelectTrigger className="w-36 input-default" data-testid="audit-actor-filter">
                <SelectValue placeholder="Actor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actors</SelectItem>
                <SelectItem value="user">Users</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="card-dashboard">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center">
                <div className="w-8 h-8 border-3 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="py-12 text-center">
                <ScrollText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">No audit logs found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800/50">
                {logs.map((log) => {
                  const config = ACTION_CONFIG[log.action] || { label: log.action, icon: FileText, color: "text-slate-400" };
                  const Icon = config.icon;
                  return (
                    <div key={log.log_id} className="flex items-start gap-4 px-5 py-4 hover:bg-slate-800/20 transition-colors" data-testid={`audit-row-${log.log_id}`}>
                      <div className={`mt-0.5 ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">{config.label}</span>
                          <Badge className={log.actor_type === "admin" ? "bg-violet-500/10 text-violet-400 border-violet-500/30 text-xs" : "bg-slate-500/10 text-slate-400 border-slate-500/30 text-xs"}>
                            {log.actor_type}
                          </Badge>
                        </div>
                        <p className="text-slate-400 text-sm mt-0.5">{log.actor_email}</p>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {Object.entries(log.details).map(([key, val]) => (
                              val && (
                                <span key={key} className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded-md">
                                  {key.replace(/_/g, " ")}: {typeof val === "object" ? JSON.stringify(val) : String(val)}
                                </span>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-slate-600 text-xs whitespace-nowrap">{formatTime(log.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-slate-500 text-sm">
              Showing {skip + 1}–{Math.min(skip + LIMIT, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="btn-secondary" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))}>
                <ChevronLeft className="w-4 h-4 mr-1" />Prev
              </Button>
              <Button variant="outline" size="sm" className="btn-secondary" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)}>
                Next<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditPage;
